/* eslint-disable react/prop-types */
import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
} from "react";

// P2-A locale split.
//
// Previously both locale dictionaries were statically imported:
//   import ar from "./lang/ar.json";  // ~114KB
//   import en from "./lang/en.json";  // ~88KB
// That put ~202KB of JSON into the main bundle, parsed on EVERY cold boot
// regardless of which language is active — a measurable chunk of the
// bootstrap-before-first-paint cost. Each language is now its own lazily
// loaded webpack chunk; only the ACTIVE language is fetched/parsed on boot,
// and the other is fetched only when the user switches to it.
//
// Behavior is otherwise identical: same t() API, same Arabic-first default,
// same setLanguage(lang) switch contract, same RTL/LTR + localStorage flow.
const localeLoaders = {
  ar: () => import(/* webpackChunkName: "locale-ar" */ "./lang/ar.json"),
  en: () => import(/* webpackChunkName: "locale-en" */ "./lang/en.json"),
};

// Module-level caches — survive Provider remounts so a language is only ever
// fetched/parsed once per session.
const dictionaries = {};
const inFlight = {};

// Idempotently load a locale into the module cache. Fail-safe: on import error
// fall back to Arabic (if already loaded), then to the previous dictionary,
// never blank. Returns a promise resolving to the dictionary object.
function loadInto(lang) {
  if (dictionaries[lang]) return Promise.resolve(dictionaries[lang]);
  if (inFlight[lang]) return inFlight[lang];
  const loader = localeLoaders[lang] || localeLoaders.ar;
  inFlight[lang] = loader()
    .then((mod) => {
      dictionaries[lang] = (mod && mod.default) || mod || {};
      return dictionaries[lang];
    })
    .catch(() => {
      // Failed to load this locale chunk: degrade gracefully to Arabic if it
      // is available, otherwise an empty dict (t() then returns raw keys —
      // exactly the previous missing-key behavior, never a crash/white screen).
      if (!dictionaries[lang]) dictionaries[lang] = dictionaries.ar || {};
      return dictionaries[lang];
    })
    .finally(() => {
      delete inFlight[lang];
    });
  return inFlight[lang];
}

// Kick the ACTIVE locale fetch off as early as possible — at module evaluation,
// which runs while the main bundle executes, before React mounts. Because the
// app's own bootstrap (provider tree + redux-persist rehydration) takes longer
// than fetching this small chunk, the active dictionary is normally ready
// before first paint, so there is no raw-key flash in practice.
try {
  const initialLang =
    (typeof localStorage !== "undefined" && localStorage.getItem("lang")) ||
    "ar";
  loadInto(initialLang === "en" ? "en" : "ar");
} catch (e) {
  loadInto("ar");
}

const TranslationContext = createContext();

export const TranslationProvider = ({ children }) => {
  const [language, setLanguageState] = useState(
    localStorage.getItem("lang") || "ar"
  );
  // Bumped whenever a dictionary finishes loading, so consumers re-render with
  // the now-available translations.
  const [revision, setRevision] = useState(0);

  const ensureDictionary = useCallback((lang) => {
    if (dictionaries[lang]) return Promise.resolve(dictionaries[lang]);
    return loadInto(lang).then((dict) => {
      setRevision((r) => r + 1);
      return dict;
    });
  }, []);

  // Ensure the active locale is loaded on mount and whenever it changes (the
  // module-level kickoff already started this for the initial language; this
  // hooks the resolution up to a re-render and covers any later change).
  useEffect(() => {
    ensureDictionary(language);
  }, [language, ensureDictionary]);

  // Switch language: load the target dictionary FIRST, then flip the active
  // language — so the UI keeps showing the current language until the new one
  // is ready (no raw-key flash on switch). Preserves the existing
  // setLanguage(lang) call contract used by header.jsx.
  const setLanguage = useCallback((lang) => {
    loadInto(lang).finally(() => setLanguageState(lang));
  }, []);

  // Identical t() behavior; the ONLY change is the dictionary lookup is guarded
  // (active dict -> Arabic -> {}) so that before a dictionary has loaded, or if
  // a load failed, it falls back to Arabic then to the raw key — the same
  // missing-key behavior as before, never a crash or blank.
  const t = useCallback(
    (key, replacements, count, asHtml = false) => {
      const dict = dictionaries[language] || dictionaries.ar || {};
      let translation = dict[key] || key;
      if (count || count == 0 || Number.isNaN(count)) {
        if (count > 1) translation = translation.more;
        else translation = translation.one;
      }
      if (replacements) {
        Object.keys(replacements).forEach((placeholder) => {
          translation = translation?.replace(
            `{${placeholder}}`,
            replacements[placeholder]
          );
        });
      }
      if (asHtml) {
        translation = { __html: translation };
      }
      return translation;
    },
    // `revision` is a dependency so consumers re-render once a dictionary
    // finishes loading (t identity changes), matching the previous
    // always-available behavior once the load completes.
    [language, revision]
  );

  const value = useMemo(() => ({ t, setLanguage }), [t, setLanguage]);

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
};

export const useTranslation = () => {
  return useContext(TranslationContext);
};
